import ListaAcoesRapidas, { TItemAcaoRapida } from "../../../../../components/ListaAcoesRapidas"
import ScreenBanner from "../../../../../components/ScreenBanner"
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Container, Grid, IconButton, Paper, Stack, Typography } from "@mui/material";
import { confirm } from "material-ui-confirm";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSetParamsURL from "../../../../../lib/useSetParamsURL";
import BtnExportar from "../../../../../components/BtnExportar";
import { TableDataGridRef, TableDataGridV2 } from "../../../../../components/TableDataGridV2";
import { processFilterValues } from "../../../../../components/TableDataGridV2/helpers";
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import PreFiltros from "../PreFiltrosListagem";
import { colunasAgrotoxicos } from "../dataGridConfig";
import { enqueueSnackbar } from "notistack";
import DialogIncluirAgrotoxico from "../DialogIncluirAgrotoxico";
import { IDetalharAgrotoxico } from "../../../../../types/api/receituarioAgronomico/detalhar-agrotoxico";
import { useModalDetalheAgrotoxico } from "../ModalDetalheAgrotoxicoContext";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../lib/axios";
import DeleteIcon from "@mui/icons-material/Delete";
import PermissionGuard, { usePermission } from "../../../../../components/PermissionGuard";

export default function Listagem() {
    const tableRef = useRef<TableDataGridRef>(null);
    const { openModal } = useModalDetalheAgrotoxico();
    const { setParamsUrl, searchParams, setMultipleParamsUrl } = useSetParamsURL()

    const [preFilter, setPreFilter] = useState(true);
    const containerRef = useRef<HTMLDivElement>(null);
    const queryClient = useQueryClient();
    const handleChangePreFilters = useCallback((value: TPreFilters) => {
        const filteredValues = processFilterValues(value, (key) => {
            filteredValues[key] = "";
        });

        setMultipleParamsUrl(filteredValues);
        if (containerRef.current) {
            containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [setMultipleParamsUrl])

    const handleClosePreFilter = useCallback(() => {
        setPreFilter(false)
    }, [])

    const [openModalIncluirEditar, setOpenModalIncluirEditar] = useState<TOpenModalAgrotoxicos | null>(null);

    const listaAcoesRapidas: Array<TItemAcaoRapida> = [
        {
            label: `Incluir agrotóxico`,
            icon: <AddIcon color="primary" />,
            action: () => handleModalAction("open", true, null),
            havePermission: usePermission("receituario.incluir-agrotoxico"),
        }
    ]

    const handleModalAction = useCallback(
        (action: "open" | "close", id?: boolean, value?: IDetalharAgrotoxico | null) => {
            if (action === "close") {
                setOpenModalIncluirEditar(null);
            } else if (action === "open") {
                setOpenModalIncluirEditar({ open: true, defaultValue: value || null });
            }
        },
        []
    );

    // Mutation for delete agrotoxico
    const { mutate: excluirAgrotoxico } = useMutation({
        mutationKey: "excluir-agrotoxico",
        mutationFn: (ids: number[]) =>
            axiosClient.delete(`/receituario-agronomico/agrotoxicos/excluir-agrotoxico`, { data: ids }),
        onError: () => {
            enqueueSnackbar("Erro ao excluir agrotóxico!", { variant: "error" });
        },
        onSuccess: () => {
            queryClient.invalidateQueries("listar-todos-agrotoxicos");
            enqueueSnackbar("Agrotóxico excluido com sucesso!", {
                variant: "success",
            });
        },
    });

    const handleDeleteSelected = useCallback(() => {
        if (tableRef.current) {
            const selectedRows: IDetalharAgrotoxico[] = tableRef.current.getSelectedRows();
            if (selectedRows.length === 0) {
                enqueueSnackbar("Por favor, selecione pelo menos um agrotóxico.", {
                    variant: "warning",
                });
                return;
            }
            const ids = selectedRows.map(row => row.id);
            confirm({ description: "Tem certeza que deseja excluir os agrotóxicos selecionados?" })
                .then(() => excluirAgrotoxico(ids))
                .catch(() => { });
        }
    }, [excluirAgrotoxico, enqueueSnackbar]);


    const RenderListagem = useMemo(() => {
        const TableComponent = () => (
            <>
                {
                    preFilter &&
                    <Box
                        minHeight={250}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <PreFiltros
                            filtros={searchParams}
                            onSubmitFilters={(values) => {
                                handleChangePreFilters(values)
                                handleClosePreFilter()
                            }}
                        />
                    </Box>
                }
                {
                    !preFilter &&
                    <>
                        <Box sx={{ display: "flex", alignItems: "flex-end", p: 2 }}>
                            <BtnExportar boxProps={{ p: 0 }} />
                            <Button sx={{ mx: 1 }} onClick={handleDeleteSelected} variant="outlined" size="small" startIcon={<DeleteIcon />}>
                                Excluir
                            </Button>
                        </Box>
                        <TableDataGridV2
                            enableSelection={true}
                            urlParams
                            ref={tableRef}
                            columns={colunasAgrotoxicos}
                            url="/receituario-agronomico/agrotoxicos/pesquisar-agrotoxico"
                            queryKey="listar-todos-agrotoxicos"
                            pagination="server"
                            queryParams={{ is_listagem: "true" }}
                            onRowDoubleClick={(id) => {
                                setParamsUrl(id.toString(), "detalhe-agrotoxico")
                                openModal(id.toString())
                            }}
                        />
                    </>
                }
            </>
        );

        return React.memo(TableComponent);
    }, [preFilter, setParamsUrl, handleChangePreFilters, handleClosePreFilter, searchParams, openModal]);

    useEffect(() => {
        if (searchParams.size > 0) {
            setPreFilter(false)
        }
    }, [searchParams])

    return (
        <>
            <ScreenBanner title="Receituário Agronômico" />
            <PermissionGuard permissionId="receituario.listar-agrotoxicos">
                <Container sx={{ mt: 3 }}>
                    <Typography textAlign="center" variant="h5">Agrotóxicos</Typography>
                    <Grid container spacing={2} mt={1}>
                        <Grid size={{xs: 12, md: 3}}>
                            <ListaAcoesRapidas
                                items={listaAcoesRapidas}
                                fullHeight={false}
                            />
                        </Grid>

                        <Grid size={{xs: 12, md: 9}}>
                            <Box component={Paper}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" px={3} pt={2}>
                                    <Typography variant="h6">Lista de agrotóxicos</Typography>

                                    {
                                        !preFilter &&
                                        <Stack alignItems="flex-end" justifyContent="center" direction="row" gap={2}>
                                            <IconButton sx={{ p: 0 }} aria-label="filtros" onClick={() => setPreFilter(true)}>
                                                <TuneOutlinedIcon />
                                            </IconButton>
                                        </Stack>
                                    }
                                </Box>
                                <RenderListagem />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
                {
                    openModalIncluirEditar?.open && (
                        <DialogIncluirAgrotoxico
                            defaultValue={openModalIncluirEditar.defaultValue}
                            open={openModalIncluirEditar.open}
                            handleClose={() => handleModalAction("close")}
                        />
                    )
                }
            </PermissionGuard>
        </>
    )
}

export type TPreFilters = {
    [key: string]: any
}

type TOpenModalAgrotoxicos = {
    open: boolean;
    defaultValue?: IDetalharAgrotoxico | null;
};